import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert
} from '@mui/material';
import { customerService, addressService } from '../services/customerService';

const emptyCustomerForm = () => ({
  company_name: '',
  short_name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  notes: ''
});

const NewCustomerModal = ({ open, onClose, onCreated }) => {
  const [formData, setFormData] = useState(emptyCustomerForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!saving) {
      setFormData(emptyCustomerForm());
      setError('');
      onClose();
    }
  };

  const handleCreate = async () => {
    const { company_name, first_name } = formData;
    if (!company_name?.trim() && !first_name?.trim()) {
      setError('Either company name or first name is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await customerService.create(formData);
      const createdCustomer = res?.data ?? res;
      const hasAddress = [formData.address_line1, formData.address_line2, formData.city, formData.state, formData.zip_code].some(
        (v) => v != null && String(v).trim() !== ''
      );
      if (createdCustomer?.id && hasAddress) {
        try {
          await addressService.create(createdCustomer.id, {
            address_type: 'billing',
            address_line1: formData.address_line1?.trim() || '',
            address_line2: formData.address_line2?.trim() || '',
            city: formData.city?.trim() || '',
            state: formData.state?.trim() || '',
            zip_code: formData.zip_code?.trim() || '',
            country: 'United States',
            is_default: true,
            notes: ''
          });
        } catch (addrErr) {
          console.error('Error creating customer address:', addrErr);
          // Customer was created; address creation is best-effort
        }
      }
      handleClose();
      onCreated?.(createdCustomer);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Customer</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Company name"
              value={formData.company_name}
              onChange={(e) => setFormData((f) => ({ ...f, company_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Short name (optional)"
              value={formData.short_name}
              placeholder="Abbreviated name for lists"
              onChange={(e) => setFormData((f) => ({ ...f, short_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="First name"
              value={formData.first_name}
              onChange={(e) => setFormData((f) => ({ ...f, first_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Last name"
              value={formData.last_name}
              onChange={(e) => setFormData((f) => ({ ...f, last_name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address line 1"
              value={formData.address_line1}
              onChange={(e) => setFormData((f) => ({ ...f, address_line1: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address line 2"
              value={formData.address_line2}
              onChange={(e) => setFormData((f) => ({ ...f, address_line2: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={formData.city}
              onChange={(e) => setFormData((f) => ({ ...f, city: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="State"
              value={formData.state}
              onChange={(e) => setFormData((f) => ({ ...f, state: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Zip code"
              value={formData.zip_code}
              onChange={(e) => setFormData((f) => ({ ...f, zip_code: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewCustomerModal;
